import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import AnimatedLogo from '../components/AnimatedLogo';
import TypewriterText from '../components/TypewriterText';
import TestimonialsSection from '../components/TestimonialsSection';
import { Bot, Code, Smartphone, Workflow, Users, Wrench } from 'lucide-react';

const services = [
  {
    icon: <Code className="w-8 h-8" />,
    title: "Web Design",
    description: "Custom websites with modern design and robust functionality",
    path: "/web-design"
  },
  {
    icon: <Smartphone className="w-8 h-8" />,
    title: "Mobile Apps",
    description: "Professional React Native apps for iOS and Android platforms",
    path: "/mobile-apps"
  },
  {
    icon: <Bot className="w-8 h-8" />,
    title: "AI Agents",
    description: "Intelligent automation and seamless customer interactions",
    path: "/ai-agents"
  },
  {
    icon: <Workflow className="w-8 h-8" />,
    title: "Automation",
    description: "Streamline and automate your business processes",
    path: "/automation"
  },
  {
    icon: <Users className="w-8 h-8" />,
    title: "Lead Generation",
    description: "Generate and nurture qualified leads at scale",
    path: "/lead-generation"
  },
  {
    icon: <Wrench className="w-8 h-8" />,
    title: "Custom Solutions",
    description: "Tailored development services for unique business needs",
    onClick: () => document.getElementById('consultation-form')?.scrollIntoView({ behavior: 'smooth' })
  }
];

export default function HomePage() {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const handleServiceClick = (service) => {
    if (service.onClick) {
      service.onClick();
    } else if (service.path) {
      navigate(service.path);
    }
  };

  return (
    <div className={theme === 'dark' ? 'bg-black' : 'bg-white'}>
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center py-20 px-4">
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="flex justify-center mb-8"
            >
              <div className="w-80 h-80">
                <AnimatedLogo />
              </div>
            </motion.div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
                Business Automation Solutions
              </span>
              <br />
              <TypewriterText
                text="Transform Your Operations"
                className={theme === 'dark' ? 'text-white' : 'text-gray-800'}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 1 }}
              className={`text-xl mb-16 max-w-2xl mx-auto ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Elevate your business with cutting-edge solutions tailored to your needs
            </motion.p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.5 }}
                  onClick={() => handleServiceClick(service)}
                  className={`${
                    theme === 'dark' ? 'bg-gray-900' : 'bg-white shadow-lg'
                  } p-6 rounded-xl cursor-pointer transform transition-all duration-300 hover:scale-105`}
                >
                  <div className="bg-blue-500/20 p-3 rounded-lg inline-block mb-4">
                    <div className="text-blue-500">{service.icon}</div>
                  </div>
                  <h3 className={`text-xl font-semibold mb-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {service.title}
                  </h3>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    {service.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <TestimonialsSection />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('consultation-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-blue-400 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:opacity-90 transition-opacity"
              >
                Discuss Your Project
              </motion.button>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}